/**
 * Seberapa sering CS mengambil alih — K207 butir 3 / W6 (SESI-58 U3b). `docs/SPEK_WA_CUSTOMER_JOURNEY.md` §9:
 * frekuensi ambil alih per langkah perjalanan, alat Owner memutuskan kapan AI dilepas penuh per langkah.
 *
 * Owner (semua cabang, bisa disaring) dan Kepala Cabang (cabangnya sendiri — dipaksa server dari profil).
 * Sumber: GET /api/wa-kanal/laporan-ambil-alih (hanya angka, tanpa nomor customer).
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { formatDate, formatPercent, isoPlusDays, todayISO } from "@/lib/format";
import { BusyButton } from "@/components/etams/BusyButton";
import { Input } from "@/components/ui/input";

// Keranjang sisa hanya tampil bila berisi — langkah perjalanan utama selalu tampil.
const LANGKAH_SISA = new Set(["Dibatalkan", "Lainnya"]);

export function formatMenit(m) {
  if (m === null || m === undefined) return "—";
  if (m < 60) return `${m} menit`;
  const j = Math.floor(m / 60);
  const s = m % 60;
  return s ? `${j} jam ${s} menit` : `${j} jam`;
}

function Angka({ judul, nilai, catatan, testId }) {
  return (
    <div className="rounded-lg border border-border p-3" data-testid={testId}>
      <p className="text-xs text-muted-foreground">{judul}</p>
      <p className="text-lg font-semibold text-foreground">{nilai}</p>
      {catatan && <p className="text-xs text-muted-foreground">{catatan}</p>}
    </div>
  );
}

function Batang({ persen }) {
  return (
    <div className="h-2 w-full rounded bg-muted" aria-hidden="true">
      <div className="h-2 rounded bg-primary" style={{ width: `${Math.min(100, Math.max(0, persen))}%` }} />
    </div>
  );
}

export default function LaporanAmbilAlihWa({ isOwner }) {
  const [isi, setIsi] = useState({ dari: isoPlusDays(-29), sampai: todayISO(), region_id: "" });
  const [d, setD] = useState(null);
  const [galat, setGalat] = useState("");
  const [sibuk, setSibuk] = useState(false);

  const muat = useCallback(async (saring) => {
    setSibuk(true);
    setGalat("");
    try {
      const params = { dari: saring.dari, sampai: saring.sampai };
      if (isOwner && saring.region_id) params.region_id = saring.region_id;
      const { data } = await api.get("/wa-kanal/laporan-ambil-alih", { params });
      setD(data);
    } catch (e) {
      setGalat(apiErrorMessage(e, "Laporan ambil alih gagal dimuat."));
    } finally {
      setSibuk(false);
    }
  }, [isOwner]);

  // Muat sekali dengan rentang bawaan (30 hari terakhir); sesudahnya lewat tombol Tampilkan.
  useEffect(() => { muat(isi); }, [muat]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = d?.total || 0;
  const waktu = d?.waktu_ditangani || {};
  const minggu = d?.per_minggu || [];
  const puncak = Math.max(1, ...minggu.map((w) => w.jumlah));

  return (
    <div className="space-y-3 text-sm" data-testid="laporan-ambil-alih">
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-end">
        <label className="space-y-1">
          <span className="block text-xs text-muted-foreground">Dari tanggal</span>
          <Input type="date" value={isi.dari} max={isi.sampai}
                 onChange={(e) => setIsi({ ...isi, dari: e.target.value })} data-testid="ambil-alih-dari" />
        </label>
        <label className="space-y-1">
          <span className="block text-xs text-muted-foreground">Sampai tanggal</span>
          <Input type="date" value={isi.sampai} min={isi.dari}
                 onChange={(e) => setIsi({ ...isi, sampai: e.target.value })} data-testid="ambil-alih-sampai" />
        </label>
        {isOwner && (
          <label className="col-span-2 space-y-1 sm:col-span-1">
            <span className="block text-xs text-muted-foreground">Wilayah</span>
            <select className="h-9 w-full rounded-md border border-input bg-background px-2" value={isi.region_id}
                    onChange={(e) => setIsi({ ...isi, region_id: e.target.value })} data-testid="ambil-alih-cabang">
              <option value="">Semua cabang</option>
              {(d?.cabang_tersedia || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
        )}
        <BusyButton className="col-span-2 sm:col-span-1" busy={sibuk} disabled={!isi.dari || !isi.sampai}
                    onClick={() => muat(isi)} data-testid="ambil-alih-tampilkan">Tampilkan</BusyButton>
      </div>

      {galat && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
          {galat}
        </div>
      )}
      {!d && !galat && <p className="text-muted-foreground">Memuat…</p>}

      {d && (
        <>
          <p className="text-xs text-muted-foreground">
            {formatDate(`${d.dari}T12:00:00`)} – {formatDate(`${d.sampai}T12:00:00`)}
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Angka judul="Diteruskan AI ke CS" nilai={`${total} kali`} testId="ambil-alih-total" />
            <Angka judul="Biasanya ditangani dalam" nilai={formatMenit(waktu.median_menit)}
                   catatan="nilai tengah" testId="ambil-alih-median" />
            <Angka judul="Paling lama ditangani" nilai={formatMenit(waktu.terlama_menit)} />
            <Angka judul="Belum ditangani lewat 1 jam kerja" nilai={`${d.terbuka_lewat_jam_kerja} kali`}
                   catatan={`dari ${d.terbuka} yang belum ditangani`} testId="ambil-alih-terlambat" />
          </div>

          {total === 0 ? (
            <p className="text-muted-foreground">Belum ada percakapan yang diteruskan ke CS pada tanggal ini.</p>
          ) : (
            <>
              <div className="space-y-1">
                <h3 className="font-medium text-foreground">Per langkah pesanan</h3>
                <table className="w-full text-xs" data-testid="ambil-alih-langkah">
                  <thead className="text-muted-foreground">
                    <tr><th className="text-left">Langkah</th><th className="text-right">Jumlah</th><th className="w-1/3 pl-2 text-left">Bagian dari semua</th></tr>
                  </thead>
                  <tbody>
                    {d.per_langkah.filter((x) => !LANGKAH_SISA.has(x.langkah) || x.jumlah > 0).map((x) => (
                      <tr key={x.langkah} className="border-t border-border">
                        <td className="py-1">{x.langkah}</td>
                        <td className="py-1 text-right">{x.jumlah}</td>
                        <td className="py-1 pl-2">
                          <div className="flex items-center gap-2">
                            <Batang persen={x.persen} />
                            <span className="w-12 shrink-0 text-right">{formatPercent(x.persen)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-1">
                <h3 className="font-medium text-foreground">Per alasan</h3>
                <table className="w-full text-xs" data-testid="ambil-alih-kategori">
                  <tbody>
                    {d.per_kategori.map((x) => (
                      <tr key={x.kategori} className="border-t border-border">
                        <td className="py-1">{x.label}</td>
                        <td className="py-1 text-right">{x.jumlah}</td>
                        <td className="w-14 py-1 text-right text-muted-foreground">{formatPercent(x.persen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-1">
                <h3 className="font-medium text-foreground">Per minggu</h3>
                <table className="w-full text-xs" data-testid="ambil-alih-minggu">
                  <tbody>
                    {minggu.map((w) => (
                      <tr key={w.minggu_mulai} className="border-t border-border">
                        <td className="whitespace-nowrap py-1 pr-2">Mulai {formatDate(`${w.minggu_mulai}T12:00:00`)}</td>
                        <td className="w-1/2 py-1"><Batang persen={(w.jumlah * 100) / puncak} /></td>
                        <td className="w-10 py-1 text-right">{w.jumlah}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {isOwner && !d.region_id && d.per_cabang.length > 0 && (
                <div className="space-y-1">
                  <h3 className="font-medium text-foreground">Per cabang</h3>
                  <table className="w-full text-xs" data-testid="ambil-alih-cabang-tabel">
                    <tbody>
                      {d.per_cabang.map((c) => (
                        <tr key={c.region_id} className="border-t border-border">
                          <td className="py-1">{c.cabang}</td>
                          <td className="py-1 text-right">{c.jumlah}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          <p className="text-xs text-muted-foreground">{d.catatan_langkah}</p>
        </>
      )}
    </div>
  );
}
